import { DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Injectable,
  computed,
  inject,
  signal,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import {
  CertificateRecord,
  PublicEvent,
  RegistrationContent,
  decodeJws,
} from "../transparency-log";
import { LocalizedText, readCredentials, readPurpose } from "../requested-data";
import {
  SIGNATURE_LABELS,
  WatchContext,
  certificateChanges,
  certificateLabel,
  entitlementLabel,
  eventLink,
  intendedUseLink,
  revocationReasonLabel,
  subjectName,
  unknownFields,
  viewLink,
} from "../watch-view-model";

interface CertificateDialogData {
  record: CertificateRecord;
  context: WatchContext;
}

/** Opens the decoded view of a published certificate. */
@Injectable({ providedIn: "root" })
export class CertificateInspector {
  private readonly dialog = inject(MatDialog);

  open(record: CertificateRecord, context: WatchContext): void {
    this.dialog.open<CertificateDialogComponent, CertificateDialogData>(
      CertificateDialogComponent,
      {
        data: { record, context },
        width: "780px",
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "calc(100vh - 24px)",
        autoFocus: "dialog",
        ariaLabel: "Certificate details",
        panelClass: "certificate-dialog",
      },
    );
  }
}

@Component({
  selector: "watch-certificate-dialog",
  imports: [DatePipe, MatDialogModule, MatIconModule],
  templateUrl: "./certificate-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificateDialogComponent {
  private readonly data = inject<CertificateDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);

  readonly context = this.data.context;
  readonly record = signal(this.data.record);
  readonly mode = signal<"decoded" | "published">("decoded");
  readonly copied = signal(false);

  readonly previous = computed(() => this.lookup(this.record().replaces));
  readonly next = computed(() => this.lookup(this.record().replacedBy));
  readonly changes = computed(() => {
    const previous = this.previous();
    return previous ? certificateChanges(previous, this.record()) : [];
  });
  readonly registration = computed(() => {
    const payload = this.record().registration;
    return payload ? readRegistration(payload) : undefined;
  });
  readonly unknownFields = computed(() => unknownFields(this.record()));
  readonly published = computed(() => publishedForm(this.record()));

  readonly label = certificateLabel;
  readonly reason = revocationReasonLabel;
  readonly signatureLabels = SIGNATURE_LABELS;

  title(): string {
    const record = this.record();
    return (
      subjectName(record) ??
      this.context.names.get(record.wrpId) ??
      record.wrpId
    );
  }

  /** Shows another certificate, e.g. the one this one replaced. */
  show(fingerprint?: string): void {
    const record = this.lookup(fingerprint);
    if (record) {
      this.record.set(record);
      this.mode.set("decoded");
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(
        this.record().issued.certificate?.encoded ?? "",
      );
      this.copied.set(true);
    } catch {
      this.copied.set(false);
    }
  }

  eventLink(event: PublicEvent): string {
    return eventLink(this.context, event);
  }

  intendedUseLink(id: string): string {
    return intendedUseLink(this.context, id);
  }

  typeLink(type: string): string {
    return viewLink("requests", type);
  }

  wrpLink(wrpId: string): string {
    return viewLink("register", wrpId);
  }

  private lookup(fingerprint?: string): CertificateRecord | undefined {
    return fingerprint ? this.context.records.get(fingerprint) : undefined;
  }
}

function readRegistration(payload: RegistrationContent) {
  const authority = record(payload.supervisory_authority);
  const intermediary = record(payload.intermediary);
  const statusList = record(record(payload.status)?.["status_list"]);
  const givenAndFamily = [text(payload.sub_gn), text(payload.sub_fn)]
    .filter(Boolean)
    .join(" ");
  return {
    name: text(payload.name),
    legalName: text(payload.sub_ln) ?? (givenAndFamily || undefined),
    identifier: text(payload.sub),
    country: text(payload.country),
    entitlements: (Array.isArray(payload.entitlements)
      ? payload.entitlements
      : []
    )
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => ({ uri: entry, label: entitlementLabel(entry) })),
    publicBody:
      typeof payload.public_body === "boolean"
        ? payload.public_body
        : undefined,
    intermediary: intermediary
      ? { sub: text(intermediary["sub"]), name: text(intermediary["sname"]) }
      : undefined,
    serviceDescriptions: serviceDescriptions(payload.srv_description),
    infoUri: text(payload.info_uri),
    supportUri: text(payload.support_uri),
    privacyPolicy: text(payload.privacy_policy),
    registryUri: text(payload.registry_uri),
    supervisoryAuthority: authority
      ? [authority["email"], authority["phone"], authority["uri"]]
          .map(text)
          .filter((value): value is string => Boolean(value))
      : [],
    issuedAt: timestamp(payload.iat),
    expiresAt: timestamp(payload.exp),
    statusList: statusList
      ? `${text(statusList["uri"]) ?? "?"} · index ${statusList["idx"]}`
      : undefined,
    jti: text(payload.jti),
    purpose: readPurpose(payload.purpose),
    credentials: readCredentials(payload.credentials),
    provides: readCredentials(payload.provides_attestations),
  };
}

/** srv_description is an array of arrays of { lang, value } in TS 119 475. */
function serviceDescriptions(value: unknown): LocalizedText[] {
  return readPurpose(Array.isArray(value) ? value.flat() : value);
}

/** The certificate as published: JWS header and payload, or PEM. */
function publishedForm(record: CertificateRecord) {
  const certificate = record.issued.certificate;
  if (!certificate) return { parts: [] };
  if (certificate.media_type === "application/jwt") {
    try {
      const { header, payload } = decodeJws(certificate.encoded);
      return {
        parts: [
          { label: "Protected header", text: JSON.stringify(header, null, 2) },
          { label: "Payload", text: JSON.stringify(payload, null, 2) },
        ],
        encoded: certificate.encoded,
      };
    } catch {
      return { parts: [], encoded: certificate.encoded };
    }
  }
  const lines = certificate.encoded.match(/.{1,64}/g) ?? [];
  return {
    parts: [
      {
        label: "PEM",
        text: [
          "-----BEGIN CERTIFICATE-----",
          ...lines,
          "-----END CERTIFICATE-----",
        ].join("\n"),
      },
    ],
  };
}

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function timestamp(value: unknown): string | undefined {
  // Outside ±8.64e15 ms a Date is invalid and toISOString() throws.
  return typeof value === "number" && Math.abs(value * 1000) <= 8.64e15
    ? new Date(value * 1000).toISOString()
    : undefined;
}
